import { Cancellation, GetAnnualPrice, PaymentBody, PaymentResponse, Promo, Subscription } from "@/models/subscription";
import axios from "axios";

/**
 * In-flight request map for restartSubscription.
 *
 * Multiple call sites can fire `restartSubscription` in the same tick
 * during user-data bootstrap (e.g. an overdue app-subscriber triggers
 * both `handleAppSubscriberUpgrade("APPDISC")` and
 * `openSubscriptionForm()` from `useGetUserData`, plus the URL
 * `?checkout=true` handler in `_app.tsx`). The backend builds a fresh
 * Stripe customer + setup-intent on every call (~3s), so duplicates
 * are visibly slow AND can race-condition the resulting plan list.
 *
 * Keying on the promo code (or "__none__") is intentional: a fresh
 * call with a different code is a real, different request and should
 * not be deduped against an in-flight call with no code.
 *
 * Once the in-flight promise settles we delete the entry so a later
 * deliberate re-fetch (e.g. user re-applying a different promo) is
 * not stuck returning a stale response.
 */
const restartSubscriptionInFlight = new Map<string, Promise<Subscription | null>>();

export const restartSubscription = async (promoCode?): Promise<Subscription | null> => {
    const dedupKey = promoCode ? String(promoCode).toUpperCase() : "__none__";
    const existing = restartSubscriptionInFlight.get(dedupKey);
    if (existing) {
        return existing;
    }

    const run = async (): Promise<Subscription | null> => {
        if (!process.env.NEXT_PUBLIC_VERCEL_ENV) {
            const dev = await Promise.resolve({
                payment_data: {
                    stripe_client_secret: "seti_1LGO94INrmKXpRLljpKXF6WS_secret_LyKoJwQymWWfIgtrxYjCnhYdLDdZUUM",
                    stripe_public_key: "pk_test_LqVdXoPabC9CkARbFE9o4vZC000J8BDipM",
                    plans: [
                        {
                            product_name: "ORTEX Advanced",
                            id: "price_1L0nBKINrmKXpRLl1J1biOC2",
                            currency: "eur",
                            price: promoCode ? 65 : 588,
                            plan_interval: "annually",
                            discount_code: "JULY4",
                            original_price: promoCode ? 111 : 588,
                            discount_value: 144,
                            discount_pc: 24.49,
                        },
                        {
                            product_name: "ORTEX Advanced",
                            id: "price_1L0n9iINrmKXpRLl6FRVltA2",
                            currency: "eur",
                            price: 79,
                            original_price: promoCode ? 111 : 588,
                            plan_interval: "monthly",
                        },
                        {
                            product_name: "ORTEX Basic",
                            id: "price_1L0nCkINrmKXpRLlG0ptAXUf",
                            currency: "eur",
                            price: 276,
                            plan_interval: "annually",
                        },
                        {
                            product_name: "ORTEX Basic",
                            id: "price_1L0nBzINrmKXpRLlAlG0eFgb",
                            currency: "eur",
                            price: 29,
                            plan_interval: "monthly",
                        },
                    ],
                    subscriptions: null,
                },
                plans: [
                    {
                        plan_id: "price_1L0nBKINrmKXpRLl1J1biOC2",
                        plan_currency: "eur",
                        plan_price: promoCode ? 111 : 588,
                        coupon: null,
                        plan_interval: "annually",
                        currency_symbol: "€",
                        plan_header: null,
                        product_name: "ORTEX Advanced",
                        plan_desc: "",
                        discount_code: "JULY4",
                        original_price: promoCode ? 111 : 588,
                        discount_value: 144,
                        currency: "eur",
                        discount_pc: 24.49,
                    },
                    {
                        plan_id: "price_1L0n9iINrmKXpRLl6FRVltA2",
                        plan_currency: "eur",
                        plan_price: 79,
                        coupon: null,
                        plan_interval: "monthly",
                        currency_symbol: "€",
                        plan_header: null,
                        product_name: "ORTEX Advanced",
                        plan_desc: "",
                    },
                    {
                        plan_id: "price_1L0nCkINrmKXpRLlG0ptAXUf",
                        plan_currency: "eur",
                        plan_price: 276,
                        coupon: null,
                        plan_interval: "annually",
                        currency_symbol: "€",
                        plan_header: null,
                        product_name: "ORTEX Basic",
                        plan_desc: "",
                    },
                    {
                        plan_id: "price_1L0nBzINrmKXpRLlAlG0eFgb",
                        plan_currency: "eur",
                        plan_price: 29,
                        coupon: null,
                        plan_interval: "monthly",
                        currency_symbol: "€",
                        plan_header: null,
                        product_name: "ORTEX Basic",
                        plan_desc: "",
                    },
                ],
                subscriptions: null,
            } as Subscription);
            return dev;
        }
        const res = await axios.get<Subscription>(promoCode ? `/API/v2/user/new_subscription?discount_code=${promoCode}` : "/API/v2/user/new_subscription");
        // Shape guard: for anonymous "visitor" users (and some error paths) the
        // endpoint answers 200 with a body that has no plans array. Returning it
        // verbatim poisons Recoil state and crashes every component that maps
        // over subscription.plans. Normalise anything without a plans array to
        // null — callers already handle "no subscription loaded".
        if (!res.data || !Array.isArray(res.data.plans)) {
            console.warn("restartSubscription: response has no plans array — treating as no subscription", res.data);
            return null;
        }
        return res.data;
    };

    const promise = run().finally(() => {
        // Clear regardless of success/failure so a retry after error
        // is not blocked, and so a deliberate later refetch is not
        // served a stale cached promise.
        restartSubscriptionInFlight.delete(dedupKey);
    });
    restartSubscriptionInFlight.set(dedupKey, promise);
    return promise;
};


export const abortCancellation = async () => {
    try {
        if (!process.env.NEXT_PUBLIC_VERCEL_ENV) {
            const res = await Promise.resolve({ message: "The cancellation is now aborted and you can continue to use ORTEX." });
            return res;
        }
        const res = await axios.post<{ message: string }>("API/v2/user/abort_cancellation");

        return res.data;
    } catch (err) {
        throw err;
    }
};

export interface WelcomeDestination {
    path: string;
    label: string;
}

/** The app page a new subscriber came for (see TED domain.purchase_welcome). */
export const getWelcomeDestination = async (): Promise<WelcomeDestination | null> => {
    if (!process.env.NEXT_PUBLIC_VERCEL_ENV) return { path: "/short-interest", label: "live short interest" };
    const res = await axios.get<WelcomeDestination>("/API/v2/user/welcome_destination");
    return res.data ?? null;
};

export const makePayment = async (body: PaymentBody) => {
    try {
        if (!process.env.NEXT_PUBLIC_VERCEL_ENV) {
            const res = await Promise.resolve({
                subscriptionId: "sub_1L5pFAINrmKXpRLlSjydgiXj",
                clientSecret: "pi_3L5pFBINrmKXpRLl0CHrsE8B_secret_Oh1JsbpRGBUGE0jmkYUGDZKg8",
            } as PaymentResponse);
            return res;
        }
        const res = await axios.post<PaymentResponse>("/API/v2/user/new_subscription", body);

        return res.data;
    } catch (err) {
        throw err;
    }
};

export const cancelBasic = async () => {
    try {
        if (!process.env.NEXT_PUBLIC_VERCEL_ENV) {
            const res = await Promise.resolve();
            return res;
        }
        const res = await axios.post("/API/v2/user/cancel_and_prorate");
        return res.data;
    } catch (err) {
        throw err;
    }
};

export const getInfoUser = async () => {
    try {
        const res = await axios.get("/payment/create_subscription");

        return res.data;
    } catch (err) {
        throw err;
    }
};

export const getPromo = async () => {
    try {
        if (!process.env.NEXT_PUBLIC_VERCEL_ENV) {
            const res = await Promise.resolve({
                promo_code: "JULY4",
                product_code: "ORTEX Advanced",
                plan_interval: "annually",
                original_price: 588,
                discount_value: 120,
                currency: "usd",
                discount_pc: 20.4,
            });
            return res;
        }
        const res = await axios.get<Promo>("/API/v2/promo");

        return res.data || {};
    } catch (err) {
        throw err;
    }
};

export const getAnnualPrice = async (): Promise<GetAnnualPrice> => {
    try {
        const res = await axios.get<GetAnnualPrice>("/interface/api/user/get_annual_price/", {
            params: null,
        });

        return res.data;
    } catch (err) {
        throw err;
    }
};

export const changeToAnnual = async () => {
    try {
        const res = await axios.post("/interface/api/user/change_to_annual/", null);

        return res.data;
    } catch (err) {
        throw err;
    }
};

